import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { LuCheck, LuCircleHelp, LuX } from 'react-icons/lu';
import { getReview, getReviewAlike, setReviewVerdict } from '@utils/api/videoProjects';
import { withArticle } from './corrections';
import { Chip, Chips, Panel } from './CountPanel';
import { keyBelongsToFocus } from './keyTarget';
import { plural } from './subjectWords';

/**
 * Checking the count track by track (backend: routers/video_review.py), on the results page itself: the player
 * jumps to the track and loops it with everything else dimmed (VideoProjectView: OverlayPlayer's spotlight), the
 * question sits under the video (ReviewQuestion) and the progress and what the answers do in the count panel
 * (ReviewSide). One track at a time, in the backend's order: the tracks with a sign of doubt, then a
 * spot check of the others (`spot`); the rest only when a spot check was wrong or the person asks. Yes / Not a … /
 * Not sure (Y / N / U, ← back, → skip, Enter done, Esc closes too; each key a keycap on its chip). A no removes the track from the
 * count as a correction (tool `review`) and every answer is kept; undoing that removal anywhere (the changes list
 * too, useCorrections) takes the answer back, so the track is asked again. Only what the page counts right now is
 * asked about (its sensitivity and size filters and the corrections). After a no, the tracks of this video that look
 * most like that one (backend review.look_alikes_of) are asked next as a group under the question, each named "Not
 * a …": the person taps the ones that are the subject and confirms the rest (A: one removal, tool `review_alike`,
 * one Undo).
 * Nothing is ticked for the person; a test (V19) decides what may be.
 */

const tk = ({ theme: t }) => t.token;
// how many look-alikes come after a no (the backend sends at most this many too)
const GROUP_SIZE = 6;
// how many tracks the Up next strip shows
const UP_NEXT = 12;

// why a track is asked about (backend review.doubts)
const WHY = {
	low_score: 'low score',
	short: 'seen under 2 s',
	odd_size: 'unusual size',
	inside_another: 'inside another box',
	at_edge: 'mostly at the edge of the picture',
};

const clock = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const removedIds = edits =>
	new Set(
		(edits || [])
			.filter(e => e.status === 'ready' && (e.type === 'remove' || e.type === 'negative'))
			.flatMap(e => e.tracks || [])
	);

// the picture of a track for a small thumbnail: its middle one
const middle = t => Math.floor((t?.crops?.length || 1) / 2);

/**
 * Everything checking the count needs, for the parts placed on the page (ReviewQuestion, ReviewSide): the
 * backend's pictures and answers, the queue, the answer keys and the group of look-alikes after a no.
 */
export const useReview = ({
	open,
	onClose,
	projectUuid,
	jobUuid,
	subject = 'object',
	count = null,
	fps = 30,
	edits = null,
	onAddEdit,
	onUndoEdit,
	onFocus,
	// a ref to what opened the check (the entry card): focus goes back to it when the check closes
	opener = null,
	// told how far the check is ({ checked, total }) whenever that changes, for the entry card
	onProgress = null,
	// the ids the page counts right now (after its sensitivity and size filters and the corrections), when known: a
	// track the filters hide is not asked about, since a no on it would change nothing the page shows
	counted = null,
	// a tour is open over the check: its own keys (arrows, Esc) must not answer, skip or close
	held = false,
}) => {
	const [retry, setRetry] = useState(false);
	const { data: review, refetch } = useQuery({
		queryKey: ['videoReview', projectUuid, jobUuid, retry],
		queryFn: () => getReview(projectUuid, jobUuid, retry),
		enabled: open && !!projectUuid && !!jobUuid,
		select: res => res?.data || null,
		refetchInterval: q => (q.state.data?.data?.status === 'preparing' ? 3000 : false),
	});
	const [verdicts, setVerdicts] = useState({});
	useEffect(() => {
		if (review?.verdicts) setVerdicts(v => ({ ...review.verdicts, ...v }));
	}, [review?.verdicts]);
	const tracks = useMemo(() => review?.tracks || [], [review?.tracks]);
	const byId = useMemo(() => new Map(tracks.map(t => [t.id, t])), [tracks]);
	const removed = useMemo(() => removedIds(edits), [edits]);
	// a removal undone in the changes list (useCorrections clears the stored answer) takes the answer back here too,
	// so the track is asked about again; only a track that *was* removed counts, so an answer whose removal is still
	// on its way to the server is left alone
	const wasRemoved = useRef(removed);
	useEffect(() => {
		const gone = [...wasRemoved.current].filter(id => !removed.has(id));
		wasRemoved.current = removed;
		if (!gone.length) return;
		setVerdicts(v => {
			const next = { ...v };
			let changed = false;
			gone.forEach(id => {
				if (next[id] === 'no') {
					delete next[id];
					changed = true;
				}
			});
			return changed ? next : v;
		});
	}, [removed]);
	// what is asked: the doubtful tracks and the spot check; every track when a spot check was wrong, when the person
	// asks for the rest, or for pictures cut before there was a plan (no track marked doubtful or spot)
	const hasPlan = tracks.some(t => t.doubt?.length > 0 || t.spot);
	const spots = useMemo(() => tracks.filter(t => t.spot), [tracks]);
	const nDoubt = tracks.filter(t => t.doubt?.length).length;
	const spotWrong = spots.some(t => verdicts[t.id] === 'no');
	const [allTracks, setAllTracks] = useState(false);
	const everyTrack = !hasPlan || allTracks || spotWrong;
	const inScope = useCallback(t => everyTrack || t.doubt?.length > 0 || t.spot, [everyTrack]);
	const nRest = tracks.length - tracks.filter(t => t.doubt?.length > 0 || t.spot).length;
	// tracks to check: not answered and not already removed, in the backend's order; "check these next", Up next and
	// back move some ahead, skip moves one to the end
	const [first, setFirst] = useState([]);
	const [later, setLater] = useState([]);
	// what back walks through, newest last: every answer and every skip ({ id, skipped })
	const [history, setHistory] = useState([]);
	// a new job (version) starts the check afresh
	const jobRef = useRef(jobUuid);
	useEffect(() => {
		if (jobRef.current === jobUuid) return;
		jobRef.current = jobUuid;
		setVerdicts({});
		setFirst([]);
		setLater([]);
		setHistory([]);
		setAllTracks(false);
		// the new version's progress is unknown until its check is opened: the card must not show the old one's
		onProgress?.(null);
	}, [jobUuid, onProgress]);
	// the question's card takes focus after an answer given with the mouse (see answer)
	const focusRef = useRef(null);
	// what can be asked about: counted on the page, or (without that knowledge) not removed
	const askable = useCallback(
		t => (counted ? counted.has(t.id) : !removed.has(t.id)),
		[counted, removed]
	);
	const queue = useMemo(() => {
		const open_ = t => !verdicts[t.id] && askable(t);
		const ahead = first.map(id => byId.get(id)).filter(t => t && open_(t));
		const behind = later
			.map(id => byId.get(id))
			.filter(t => t && open_(t) && inScope(t) && !first.includes(t.id));
		const moved = new Set([...first, ...later]);
		const rest = tracks.filter(t => open_(t) && inScope(t) && !moved.has(t.id));
		return [...ahead, ...rest, ...behind];
	}, [tracks, byId, verdicts, askable, first, later, inScope]);
	const current = queue[0] || null;
	useEffect(() => {
		if (open && current) onFocus?.(current);
	}, [open, current, onFocus]);

	const no = useMemo(
		() =>
			Object.keys(verdicts)
				.filter(k => verdicts[k] === 'no')
				.map(Number),
		[verdicts]
	);
	const yes = useMemo(
		() =>
			Object.keys(verdicts)
				.filter(k => verdicts[k] === 'yes')
				.map(Number),
		[verdicts]
	);
	// the group after a no: the tracks that look most like that one, asked next. Fixed when the no is given (with the
	// answers so far), so it does not shift under the person's hand as they answer; a member said no to leaves it,
	// one said yes to or not sure about stays with its mark. It ends when none is left unanswered, or when the no it
	// came from is taken back.
	const alikeAvailable = !!review?.alike_available;
	const [group, setGroup] = useState(null);
	const { data: groupData } = useQuery({
		queryKey: ['videoReviewGroup', projectUuid, jobUuid, group],
		queryFn: () => getReviewAlike(projectUuid, jobUuid, group),
		enabled: open && !!group,
		staleTime: Infinity,
	});
	const groupIds = useMemo(
		() => (groupData?.data?.proposals || []).slice(0, GROUP_SIZE).map(p => p.id),
		[groupData]
	);
	// the group holds while the no it came from does
	const groupOn = !!group && verdicts[group.of] === 'no';
	const members = useMemo(() => {
		if (!groupOn) return [];
		return groupIds
			.map(id => byId.get(id))
			.filter(t => t && (verdicts[t.id] ? verdicts[t.id] !== 'no' : askable(t)));
	}, [groupOn, groupIds, byId, verdicts, askable]);
	const rest = useMemo(
		() => members.filter(t => !verdicts[t.id]).map(t => t.id),
		[members, verdicts]
	);
	// the look-alikes are asked next, before the rest; when the no is taken back they go back to their place
	useEffect(() => {
		if (!groupIds.length) return;
		if (groupOn) {
			setFirst(f => [...groupIds, ...f.filter(x => !groupIds.includes(x))]);
			setLater(l => l.filter(x => !groupIds.includes(x)));
		} else setFirst(f => f.filter(x => !groupIds.includes(x)));
	}, [groupOn, groupIds]);

	const answer = useCallback(
		(track, verdict) => {
			if (!track) return;
			const before = verdicts[track.id];
			setVerdicts(v => ({ ...v, [track.id]: verdict }));
			setHistory(h => [...h, { id: track.id }]);
			// an answer clicked with the mouse leaves its chip focused, and the next Space or Enter would press it
			// again for the next track: focus moves to the card, where Space reaches the player as before
			if (document.activeElement?.tagName === 'BUTTON')
				focusRef.current?.focus({ preventScroll: true });
			setReviewVerdict(projectUuid, jobUuid, track.id, verdict).catch(() => {});
			// a no on a track outside the group starts a new one: what looks like it comes next
			if (verdict === 'no' && alikeAvailable && !groupIds.includes(track.id))
				setGroup({ of: track.id, no: [...no, track.id], yes });
			if (verdict === 'no' && !removed.has(track.id))
				onAddEdit({
					type: 'negative',
					tracks: [track.id],
					frame: Math.round(track.t0 * fps),
					tool: 'review',
				});
			if (before === 'no' && verdict !== 'no') {
				const edit = (edits || []).find(
					e => e.tool === 'review' && (e.tracks || []).includes(track.id) && !e.pending
				);
				if (edit) onUndoEdit(edit.id);
			}
		},
		[
			verdicts,
			removed,
			edits,
			projectUuid,
			jobUuid,
			fps,
			onAddEdit,
			onUndoEdit,
			alikeAvailable,
			groupIds,
			no,
			yes,
		]
	);
	// a tap on a look-alike says it *is* the subject (a yes); a second tap takes that back
	const mark = useCallback(
		track => {
			if (!track) return;
			if (verdicts[track.id] !== 'yes') answer(track, 'yes');
			else {
				setVerdicts(v => {
					const next = { ...v };
					delete next[track.id];
					return next;
				});
				setHistory(h => {
					const i = h.map(x => x.id).lastIndexOf(track.id);
					return i < 0 ? h : [...h.slice(0, i), ...h.slice(i + 1)];
				});
				setReviewVerdict(projectUuid, jobUuid, track.id, null).catch(() => {});
			}
			if (document.activeElement?.tagName === 'BUTTON')
				focusRef.current?.focus({ preventScroll: true });
		},
		[verdicts, answer, projectUuid, jobUuid]
	);
	// the group's unanswered ones are not the subject: a no for each, taken out together as one removal (one Undo)
	const takeRest = useCallback(() => {
		if (!rest.length) return;
		const ids = rest;
		setVerdicts(v => ({ ...v, ...Object.fromEntries(ids.map(id => [id, 'no'])) }));
		setHistory(h => [...h, { ids, group }]);
		ids.forEach(id => setReviewVerdict(projectUuid, jobUuid, id, 'no').catch(() => {}));
		const out = ids.filter(id => !removed.has(id));
		if (out.length)
			onAddEdit({
				type: 'negative',
				tracks: out,
				frame: Math.round(Math.min(...out.map(id => byId.get(id)?.t0 ?? 0)) * fps),
				tool: 'review_alike',
			});
		if (document.activeElement?.tagName === 'BUTTON')
			focusRef.current?.focus({ preventScroll: true });
	}, [rest, group, removed, byId, fps, projectUuid, jobUuid, onAddEdit]);
	// back brings the last track up again: a skipped one as it was, an answered one with its answer taken back
	const back = useCallback(() => {
		const last = history[history.length - 1];
		if (!last) return;
		const { id, skipped, ids } = last;
		setHistory(h => h.slice(0, -1));
		if (ids) {
			const edit = (edits || []).find(
				e =>
					e.tool === 'review_alike' &&
					!e.pending &&
					ids.every(x => (e.tracks || []).includes(x))
			);
			setVerdicts(v => {
				const next = { ...v };
				ids.forEach(x => delete next[x]);
				return next;
			});
			if (edit) onUndoEdit(edit.id);
			else ids.forEach(x => setReviewVerdict(projectUuid, jobUuid, x, null).catch(() => {}));
			setGroup(last.group);
			setFirst(f => [...ids, ...f.filter(x => !ids.includes(x))]);
			setLater(l => l.filter(x => !ids.includes(x)));
			return;
		}
		if (!skipped) {
			const edit = (edits || []).find(
				e => e.tool === 'review' && (e.tracks || []).includes(id) && !e.pending
			);
			setVerdicts(v => {
				const next = { ...v };
				delete next[id];
				return next;
			});
			// undoing the removal takes the stored answer back with it (useCorrections); a yes or not sure has no
			// removal, so the answer is taken back here
			if (edit) onUndoEdit(edit.id);
			else setReviewVerdict(projectUuid, jobUuid, id, null).catch(() => {});
		}
		setFirst(f => [id, ...f.filter(x => x !== id)]);
		setLater(l => l.filter(x => x !== id));
	}, [history, edits, projectUuid, jobUuid, onUndoEdit]);
	const skip = useCallback(() => {
		if (!current) return;
		setHistory(h => [...h, { id: current.id, skipped: true }]);
		setFirst(f => f.filter(x => x !== current.id));
		setLater(l => [...l.filter(x => x !== current.id), current.id]);
	}, [current]);
	// check these next (the look-alikes, a track from Up next): ahead of the rest, in this order
	const checkNext = useCallback(ids => {
		setFirst(f => [...ids, ...f.filter(x => !ids.includes(x))]);
		setLater(l => l.filter(x => !ids.includes(x)));
	}, []);

	// Done (Enter) and Escape both come through here; focus goes back to what opened the check
	const close = useCallback(() => {
		onClose?.();
		opener?.current?.focus();
	}, [onClose, opener]);

	useEffect(() => {
		if (!open || held) return undefined;
		const onKey = e => {
			if (keyBelongsToFocus(e)) return;
			// the player in full screen leaves the question out of sight: no answer blind, and Escape stays the
			// browser's (it leaves full screen)
			const fs = document.fullscreenElement;
			if (fs && !fs.contains(focusRef.current)) return;
			// a key with Ctrl, Alt or Cmd is the browser's or the page's; Shift+arrow is the player's 1 s seek
			if (e.ctrlKey || e.metaKey || e.altKey) return;
			const k = e.key.toLowerCase();
			if (e.shiftKey && k.startsWith('arrow')) return;
			const act = {
				y: () => answer(current, 'yes'),
				n: () => answer(current, 'no'),
				u: () => answer(current, 'unsure'),
				a: rest.length ? takeRest : null,
				arrowleft: back,
				arrowright: skip,
				// done is complete, so Enter; on a focused chip Enter presses the chip instead (keyBelongsToFocus)
				enter: close,
				// the check is what is open, so Escape closes it and nothing else: the page's own Escape (leave
				// correcting) never sees it
				escape: close,
			}[k];
			if (!act) return;
			e.preventDefault();
			e.stopPropagation();
			act();
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [open, held, current, answer, back, skip, close, rest, takeRest]);

	// what can be checked: what is asked (the plan) with an answer, or not removed some other way (a track taken out
	// by the pencil or a dot cannot be asked about, so it is not part of the total either); answers given before the
	// plan may be on tracks it leaves out
	const total = tracks.filter(t => inScope(t) && (verdicts[t.id] || askable(t))).length;
	const checked = tracks.filter(t => inScope(t) && verdicts[t.id]).length;
	useEffect(() => {
		if (tracks.length) onProgress?.({ checked, total });
	}, [checked, total, tracks.length, onProgress]);
	const tally = Object.values(verdicts).reduce((t, v) => ({ ...t, [v]: (t[v] || 0) + 1 }), {
		yes: 0,
		no: 0,
		unsure: 0,
	});
	// how many tracks this check has taken out of the count so far (its own corrections, still in place)
	const byTool = tool =>
		(edits || [])
			.filter(e => e.tool === tool && e.status === 'ready' && !e.pending)
			.reduce((n, e) => n + (e.tracks || []).length, 0);
	return {
		open,
		onClose: close,
		focusRef,
		status: review?.status,
		message: review?.message,
		alikeAvailable,
		retryPrep: () => {
			setRetry(true);
			refetch();
		},
		subject,
		notThe: `Not ${withArticle(subject)}`,
		count,
		tracks,
		byId,
		verdicts,
		queue,
		current,
		answer,
		back,
		skip,
		canGoBack: history.length > 0,
		toGo: queue.length,
		checkNext,
		hasPlan,
		spots,
		nDoubt,
		nRest,
		spotWrong,
		allTracks,
		everyTrack,
		checkAll: () => setAllTracks(true),
		total,
		checked,
		tally,
		group: rest.length ? { of: byId.get(group.of), members, rest } : null,
		mark,
		takeRest,
		removedNow: byTool('review') + byTool('review_alike'),
		togetherNow: byTool('review_alike'),
	};
};

const Muted = styled.div`
	color: ${p => tk(p).colorTextTertiary};
	font-size: ${p => tk(p).fontSizeSM}px;
`;

const Bar = styled.div`
	height: ${p => tk(p).marginXXS}px;
	background: ${p => tk(p).colorBorderSecondary};
	border-radius: ${p => tk(p).borderRadiusXS}px;
	overflow: hidden;
	& > span {
		display: block;
		height: 100%;
		background: ${p => tk(p).colorPrimary};
	}
`;

const Head = styled.div`
	font-weight: ${p => tk(p).fontWeightStrong};
	color: ${p => tk(p).colorText};
`;

// a picture with the track's box drawn where it is (backend review.json: boxes, shares of the picture's side)
const Pic = styled.div`
	position: relative;
	& > img {
		width: 100%;
		aspect-ratio: 1;
		border-radius: ${p => tk(p).borderRadiusLG}px;
		display: block;
	}
	& > i {
		position: absolute;
		pointer-events: none;
		border: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorWarning}`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		box-shadow: ${p => `0 0 0 ${tk(p).lineWidth}px ${tk(p).colorBgMask}`};
	}
	& > em {
		position: absolute;
		left: 50%;
		bottom: ${p => tk(p).marginXXS}px;
		transform: translateX(-50%);
		max-width: 92%;
		box-sizing: border-box;
		text-align: center;
		line-height: 1.25;
		font-style: normal;
		font-size: ${p => tk(p).fontSizeSM}px;
		color: ${p => tk(p).colorText};
		background: ${p => tk(p).colorBgMask};
		border-radius: ${p => tk(p).fontSizeSM * 2}px;
		padding: ${p => `0 ${tk(p).paddingXS}px`};
		pointer-events: none;
	}
`;

// the picture shows only part of the object: the backend says so (`cut`); pictures cut before it did show it by
// their box reaching the picture's side (the square around the box only stops short of the box at the video's edge)
const EDGE = 0.01;
const isCut = (track, i) => {
	if (track.cut?.[i] != null) return track.cut[i];
	const b = track.boxes?.[i];
	return !!b && (b[0] <= EDGE || b[1] <= EDGE || b[2] >= 1 - EDGE || b[3] >= 1 - EDGE);
};

const boxAt = b => ({
	left: `${b[0] * 100}%`,
	top: `${b[1] * 100}%`,
	width: `${(b[2] - b[0]) * 100}%`,
	height: `${(b[3] - b[1]) * 100}%`,
});

const Picture = ({ track, i, saysCut = false }) => (
	<Pic>
		<img src={track.crops[i]} alt="" />
		{track.boxes?.[i] && <i style={boxAt(track.boxes[i])} />}
		{saysCut && isCut(track, i) && <em>Cut by the edge</em>}
	</Pic>
);

// the answers: the count panel's own pill chips at their own size (Y / N / U carry the repetition); a no in the
// error colour
const Answer = Chip;

// the answers and the way through them, each with its key as a keycap on the chip (CorrectionToolbar does the same)
const KeyedChips = styled(Chips)`
	kbd {
		/* Bootstrap paints kbd as a filled block; the keycap is an outline in the chip's own colour */
		background: transparent;
		margin-left: ${p => tk(p).marginXXS}px;
		font-size: ${p => tk(p).fontSizeXS}px;
		line-height: 1;
		color: inherit;
		opacity: 0.9;
		border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} currentColor`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXXS}px`};
	}
`;

const NoAnswer = styled(Answer)`
	border-color: ${p => tk(p).colorError};
	color: ${p => tk(p).colorError};
	&:hover:not(:disabled) {
		border-color: ${p => tk(p).colorError};
		background: ${p => tk(p).colorErrorBg};
	}
`;

// the card under the video: the count panel's surface, so the page keeps one look
const Under = styled(Panel)`
	height: 100%;
	box-sizing: border-box;
	/* focused only by script, after a mouse answer: no ring on a container */
	outline: none;
	padding: ${p => `${tk(p).paddingSM}px ${tk(p).padding}px`};
	gap: ${p => tk(p).marginSM}px;
	overflow: auto;
`;

const Top = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 3fr) minmax(0, 4fr);
	gap: ${p => tk(p).margin}px;
	align-items: start;
	@media (max-width: ${p => tk(p).screenMD}px) {
		grid-template-columns: 1fr;
	}
`;

const Pics = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: ${p => tk(p).marginXS}px;
	& button {
		all: unset;
		cursor: pointer;
		display: flex;
		flex-direction: column;
		gap: ${p => tk(p).marginXXS}px;
		text-align: center;
		color: ${p => tk(p).colorTextTertiary};
		font-size: ${p => tk(p).fontSizeSM}px;
	}
	& button:focus-visible img {
		outline: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorPrimary}`};
	}
`;

const Ask = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
	min-width: 0;
`;

const Question = styled.div`
	font-size: ${p => tk(p).fontSizeLG}px;
	color: ${p => tk(p).colorText};
`;

const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
`;

const Thumbs = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	overflow-x: auto;
	& button {
		all: unset;
		cursor: pointer;
		flex: none;
		width: ${p => tk(p).controlHeightLG + tk(p).marginXS}px;
	}
	& button:focus-visible img {
		outline: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorPrimary}`};
	}
`;

// the look-alikes after a no: the page's rounded card in the warning colour, so it stands out by colour alone
const Group = styled.div`
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorWarningBorder}`};
	background: ${p => tk(p).colorWarningBg};
	border-radius: ${p => tk(p).borderRadiusLG * 2}px;
	padding: ${p => tk(p).paddingSM}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginSM}px;
`;

const Tiles = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: ${p => tk(p).marginSM}px;
`;

// one look-alike: its clearest picture, big enough to judge, and what it is called now under it
const Tile = styled.button`
	all: unset;
	box-sizing: border-box;
	cursor: pointer;
	width: ${p => tk(p).controlHeightLG * 3}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
	text-align: center;
	color: ${p => tk(p).colorTextTertiary};
	font-size: ${p => tk(p).fontSizeSM}px;
	& img {
		outline-offset: ${p => tk(p).lineWidth}px;
	}
	&:focus-visible img {
		outline: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorPrimary}`};
	}
	/* the one on the card now */
	&[aria-current='true'] img {
		outline: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorText}`};
	}
	&[aria-pressed='true'] img {
		outline: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorSuccess}`};
	}
`;

// the name on a tile: "Not a reindeer" in outline until confirmed (filled on the one the person said no to),
// "Reindeer" once tapped, "Not sure" from the card
const Name = styled.span`
	border-radius: ${p => tk(p).fontSizeSM * 2}px;
	padding: ${p => `${tk(p).lineWidth}px ${tk(p).paddingXS}px`};
	font-weight: ${p => tk(p).fontWeightStrong};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorError}`};
	color: ${p => tk(p).colorError};
	&[data-tone='said'] {
		background: ${p => tk(p).colorError};
		color: ${p => tk(p).colorTextLightSolid};
	}
	&[data-tone='yes'] {
		border-color: ${p => tk(p).colorSuccess};
		background: ${p => tk(p).colorSuccess};
		color: ${p => tk(p).colorTextLightSolid};
	}
	&[data-tone='unsure'] {
		border-color: ${p => tk(p).colorWarning};
		background: ${p => tk(p).colorWarning};
		color: ${p => tk(p).colorTextLightSolid};
	}
`;

const GroupWords = styled.div`
	flex: 1 1 ${p => tk(p).controlHeightLG * 8}px;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
`;

// between the one said no to and its look-alikes
const Split = styled.span`
	align-self: stretch;
	width: ${p => tk(p).lineWidth}px;
	background: ${p => tk(p).colorWarningBorder};
`;

const capital = w => (w ? w[0].toUpperCase() + w.slice(1) : w);

/**
 * After a no: the tracks that look the same, each named "Not a …" until the person taps the ones that are the
 * subject; Confirm (A) takes the ones still named "Not a …" out together.
 */
const LookAlikes = ({ r }) => {
	const g = r.group;
	const not = `Not ${withArticle(r.subject)}`;
	const n = g.rest.length;
	return (
		<Group
			data-tour="video-check-alike"
			aria-label="Tracks that look the same as the one you said no to">
			<Row>
				<GroupWords>
					<Head>
						These look the same as the one you said is not {withArticle(r.subject)}
					</Head>
					<Muted>Tap any that is {withArticle(r.subject)}, then confirm.</Muted>
				</GroupWords>
				<KeyedChips>
					<NoAnswer type="button" onClick={r.takeRest}>
						Confirm · take {n === 1 ? 'this one' : `these ${n}`} out <kbd>A</kbd>
					</NoAnswer>
				</KeyedChips>
			</Row>
			<Tiles>
				{g.of && (
					<Tile as="div">
						<Picture track={g.of} i={middle(g.of)} />
						<Name data-tone="said">{not}</Name>
					</Tile>
				)}
				<Split aria-hidden />
				{g.members.map(t => {
					const v = r.verdicts[t.id];
					return (
						<Tile
							key={t.id}
							type="button"
							aria-pressed={v === 'yes'}
							aria-current={r.current?.id === t.id}
							title={`#${t.id} · ${clock(t.t0)} · tap if it is ${withArticle(r.subject)}`}
							onClick={() => r.mark(t)}>
							<Picture track={t} i={middle(t)} />
							<Name data-tone={v || null}>
								{v === 'yes'
									? capital(r.subject)
									: v === 'unsure'
										? 'Not sure'
										: not}
							</Name>
						</Tile>
					);
				})}
			</Tiles>
		</Group>
	);
};

/** The question, under the video: the track's pictures, Yes / Not a … / Not sure, and what comes next. */
/** `help`: the check's tour button, placed beside Done while a track is asked about. */
export const ReviewQuestion = ({ r, onSeek, help = null }) => {
	const { status, current } = r;
	if (status === 'failed')
		return (
			<Under as="section" ref={r.focusRef} tabIndex={-1} aria-label="Check the count">
				<Muted>{r.message || 'The pictures could not be made.'}</Muted>
				<Row>
					<Button onClick={r.retryPrep}>Try again</Button>
					<Chip type="button" onClick={r.onClose}>
						Done
					</Chip>
				</Row>
			</Under>
		);
	if (status === 'too_long')
		return (
			<Under as="section" ref={r.focusRef} tabIndex={-1} aria-label="Check the count">
				<Muted>{r.message}</Muted>
			</Under>
		);
	if (status !== 'ready')
		return (
			<Under
				as="section"
				ref={r.focusRef}
				tabIndex={-1}
				aria-label="Check the count"
				aria-busy="true">
				<Muted>Getting the pictures of every track ready…</Muted>
			</Under>
		);
	if (!current)
		return (
			<Under as="section" ref={r.focusRef} tabIndex={-1} aria-label="Check the count">
				{!r.everyTrack && r.nRest > 0 ? (
					<>
						<Head>The spot check found nothing wrong</Head>
						<Muted>
							None of the {r.spots.length} spot checks was a mistake, so the other{' '}
							{r.nRest} tracks look sure and are left as they are.
						</Muted>
						<KeyedChips>
							<Answer type="button" onClick={r.checkAll}>
								Check them anyway
							</Answer>
							<Answer type="button" onClick={r.back} disabled={!r.canGoBack}>
								Back <kbd>←</kbd>
							</Answer>
							<Answer type="button" onClick={r.onClose}>
								Done <kbd>Enter</kbd>
							</Answer>
						</KeyedChips>
					</>
				) : (
					<>
						<Head>All tracks checked</Head>
						<Muted>
							Your answers are kept to improve the model next time; the ones you said
							no to are in Your changes.
						</Muted>
						<KeyedChips>
							<Answer type="button" onClick={r.back} disabled={!r.canGoBack}>
								Back <kbd>←</kbd>
							</Answer>
							<Answer type="button" onClick={r.onClose}>
								Done <kbd>Enter</kbd>
							</Answer>
						</KeyedChips>
					</>
				)}
			</Under>
		);
	const upNext = r.queue.slice(1, 1 + UP_NEXT);
	const subject = withArticle(current.label || r.subject);
	const answers = (
		<KeyedChips data-tour="video-check-answer">
			<Answer type="button" $active onClick={() => r.answer(current, 'yes')}>
				<LuCheck /> Yes <kbd>Y</kbd>
			</Answer>
			{/* the question beside it names the subject, so the chip is just No; the long form stays where there is
			    no question beside it (the answers card, the changes list) */}
			<NoAnswer type="button" onClick={() => r.answer(current, 'no')}>
				<LuX /> No <kbd>N</kbd>
			</NoAnswer>
			<Answer type="button" onClick={() => r.answer(current, 'unsure')}>
				<LuCircleHelp /> Not sure <kbd>U</kbd>
			</Answer>
		</KeyedChips>
	);
	// back, skip and done: on screen as well as on the keys; back has nothing to go to at the start
	const moves = (
		<KeyedChips data-tour="video-check-nav">
			<Answer type="button" onClick={r.back} disabled={!r.canGoBack}>
				Back <kbd>←</kbd>
			</Answer>
			<Answer type="button" onClick={r.skip} disabled={r.toGo < 2}>
				Skip <kbd>→</kbd>
			</Answer>
			<Answer type="button" onClick={r.onClose}>
				Done <kbd>Enter</kbd>
			</Answer>
			{help}
		</KeyedChips>
	);
	// while the look-alikes are up they are the pictures: the question is one line over them, the track it asks
	// about ringed among them and playing in the video
	if (r.group)
		return (
			<Under as="section" ref={r.focusRef} tabIndex={-1} aria-label="Check the count">
				<Row>
					<Head>
						#{current.id} · {clock(current.t0)}–{clock(current.t1)}
					</Head>
					<Question>Is this {subject}?</Question>
					{answers}
					{moves}
				</Row>
				<LookAlikes r={r} />
			</Under>
		);
	return (
		<Under as="section" ref={r.focusRef} tabIndex={-1} aria-label="Check the count">
			<Top>
				<Pics data-tour="video-check-pictures">
					{current.crops.map((c, i) => (
						<button
							key={i}
							type="button"
							title="Show this moment"
							onClick={() =>
								current.times?.[i] != null && onSeek?.(current.times[i])
							}>
							<Picture track={current} i={i} saysCut />
							{current.times?.[i] != null ? clock(current.times[i]) : ''}
						</button>
					))}
				</Pics>
				<Ask>
					<Head>
						#{current.id} · {clock(current.t0)}–{clock(current.t1)}
						<Muted as="span"> · score {current.conf.toFixed(2)}</Muted>
					</Head>
					{current.doubt?.length > 0 && (
						<Muted>
							Asked because: {current.doubt.map(d => WHY[d] || d).join(' · ')}
						</Muted>
					)}
					{current.spot && !current.doubt?.length && (
						<Muted>Spot check: nothing about it looks doubtful</Muted>
					)}
					<Question>Is this {subject}?</Question>
					<Muted>
						Answer for the whole track, not one picture: if most of them show {subject},
						say Yes. Not sure keeps it counted.
					</Muted>
					{answers}
					{moves}
				</Ask>
			</Top>
			{upNext.length > 0 && (
				<Row data-tour="video-check-up-next">
					<Muted>Up next · {r.queue.length - 1}</Muted>
					<Thumbs>
						{upNext.map(t => (
							<button
								key={t.id}
								type="button"
								title={`#${t.id} · ${clock(t.t0)}`}
								onClick={() => r.checkNext([t.id])}>
								<Picture track={t} i={middle(t)} />
							</button>
						))}
					</Thumbs>
				</Row>
			)}
		</Under>
	);
};

const SideBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginSM}px;
	color: ${p => tk(p).colorText};
`;

// the progress lines the tour points at, spaced as the rest of the box
const SidePart = styled(SideBox)``;

const Card = styled.div`
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	border-radius: ${p => tk(p).borderRadiusLG * 2}px;
	padding: ${p => tk(p).paddingSM}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
`;

const Count = styled.div`
	font-size: ${p => tk(p).fontSizeHeading3}px;
	font-weight: ${p => tk(p).fontWeightStrong};
`;

const Points = styled.ul`
	margin: 0;
	padding-left: ${p => tk(p).paddingMD}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
	font-size: ${p => tk(p).fontSizeSM}px;
	color: ${p => tk(p).colorTextSecondary};
`;

/** The count panel's part while checking: how far, and what the answers do. */
export const ReviewSide = ({ r }) => (
	<SideBox aria-label="Check the count: progress">
		<SidePart data-tour="video-check-progress">
			<Head>Check the count</Head>
			<Muted>
				{r.checked} of {r.tracks.length ? r.total : '…'} checked
				{r.current ? ` · ${r.toGo} to go` : ''} · work in progress
			</Muted>
			{r.tracks.length > 0 && (
				<Bar aria-hidden>
					<span style={{ width: `${r.total ? (100 * r.checked) / r.total : 0}%` }} />
				</Bar>
			)}
			{r.hasPlan && (
				<Muted>
					{r.nDoubt} doubtful, then {r.spots.length} spot checks
					{r.everyTrack && r.nRest > 0 ? `, then the other ${r.nRest}` : ''}
				</Muted>
			)}
			{r.hasPlan && r.spotWrong && !r.allTracks && r.nRest > 0 && (
				<Muted>A spot check was wrong, so the other tracks are asked about too.</Muted>
			)}
		</SidePart>
		{r.status === 'ready' && (
			<Card data-tour="video-check-effect">
				<Head>What your answers do</Head>
				{r.count != null && (
					<Count>
						{r.count}
						<Muted as="span">
							{' counted now'}
							{r.removedNow > 0 ? ` · ${r.removedNow} taken out by you` : ''}
						</Muted>
					</Count>
				)}
				<Points>
					<li>
						{r.notThe} takes the track out of the count at once
						{r.tally.no
							? ` (${r.tally.no} by you${r.togetherNow ? `, ${r.togetherNow} of them together` : ''})`
							: ''}
						.
					</li>
					{r.alikeAvailable && (
						<li>
							After a No, the tracks that look the same come next: tap any that are{' '}
							{plural(r.subject)}, then confirm the rest.
						</li>
					)}
					<li>
						Yes and Not sure keep it counted ({r.tally.yes} yes, {r.tally.unsure} not
						sure).
					</li>
					<li>
						Every removal is in Your changes, where Undo puts it back; Back goes back
						one step.
					</li>
					<li>
						Your answers are kept with this video, and we use them to improve the model
						next time. Nothing else is changed.
					</li>
				</Points>
			</Card>
		)}
	</SideBox>
);

/** Both parts together, as the page places them: for tests and for a page without a stage to split them on. */
const ReviewMode = ({ onSeek, ...props }) => {
	const r = useReview(props);
	return (
		<>
			<ReviewQuestion r={r} onSeek={onSeek} />
			<ReviewSide r={r} />
		</>
	);
};

// the way in, under the count: a card of its own colour, not one more grey button
const EntryCard = styled.button`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${p => tk(p).marginXXS}px;
	width: 100%;
	padding: ${p => `${tk(p).paddingSM}px ${tk(p).padding}px`};
	cursor: pointer;
	font: inherit;
	text-align: left;
	color: ${p => tk(p).colorText};
	background: ${p => tk(p).colorPrimaryBg};
	border: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorPrimaryBorder}`};
	border-radius: ${p => tk(p).borderRadiusLG * 2}px;
	& b {
		color: ${p => tk(p).colorPrimary};
		display: flex;
		align-items: center;
		gap: ${p => tk(p).marginXXS}px;
	}
	& span {
		font-size: ${p => tk(p).fontSizeSM}px;
		color: ${p => tk(p).colorTextSecondary};
	}
	&:hover {
		border-color: ${p => tk(p).colorPrimary};
	}
`;

// about 4 s a track
const minutes = n => Math.max(1, Math.round((n * 4) / 60));

// ref: the card itself, so the check can give focus back to it (React 19 passes ref as a prop)
// progress: { checked, total } from the check once it has the review (null until it is opened in this visit). Until
// then only the number of tracks is known; after, the total is what the check asks about (the doubtful ones and a
// spot check), often far fewer
export const ReviewEntry = ({ tracks, onOpen, ref = null, progress = null }) => {
	const done = progress && progress.total > 0 && progress.checked >= progress.total;
	let line = `${tracks} ${tracks === 1 ? 'track' : 'tracks'} · about ${minutes(tracks)} min`;
	if (done) line = `All ${progress.total} checked · open to look again`;
	else if (progress?.total && !progress.checked)
		line = `${progress.total} to check · about ${minutes(progress.total)} min`;
	else if (progress?.checked)
		line = `${progress.checked} of ${progress.total} checked · about ${minutes(progress.total - progress.checked)} min left`;
	return (
		<EntryCard ref={ref} type="button" data-tour="video-review" onClick={onOpen}>
			<b>
				<LuCheck /> {done ? 'Count checked' : 'Check the count'}
			</b>
			<span>{line}</span>
		</EntryCard>
	);
};

export default ReviewMode;
